import * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {IonIcon} from '@ionic/react';
import {
    addCircleOutline,
    addCircleSharp,
    hardwareChipOutline,
    hardwareChipSharp,
    personCircleOutline,
    personCircleSharp,
    sendOutline,
    sendSharp,
    trashSharp,
} from 'ionicons/icons';
import {ThreeDot} from 'react-loading-indicators';

import Header from '../../components/Header/Header';
import ChatChart, {ChartSpec} from './ChatChart';
import {
    Conversation,
    TOOL_LABELS,
    deleteConversation,
    getMessages,
    listConversations,
    streamChat,
} from './chatApi';
import './TitanBot.css';

interface Bubble {
    role: 'user' | 'assistant';
    content: string;
    tools?: string[];
    charts?: ChartSpec[];
    snapshots?: {title: string; image_url: string}[];
    error?: boolean;
}

const SUGGESTIONS = [
    "What's happening on Missouri roads right now?",
    'Which corridors are most congested?',
    'Any flooding or weather affecting roads?',
    'Summarise the last few hours',
];

const TitanBot: React.FC = () => {
    const [bubbles, setBubbles] = useState<Bubble[]>([]);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [userinput, setUserinput] = useState('');
    const [busy, setBusy] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const refreshConversations = useCallback(() => {
        listConversations().then(setConversations).catch(() => setConversations([]));
    }, []);

    useEffect(() => { refreshConversations(); }, [refreshConversations]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    }, [bubbles]);

    const openConversation = async (id: string) => {
        setConversationId(id);
        const stored = await getMessages(id);
        setBubbles(stored.map(m => ({role: m.role, content: m.content})));
    };

    const removeConversation = async (id: string) => {
        await deleteConversation(id);
        if (id === conversationId) {
            setConversationId(null);
            setBubbles([]);
        }
        refreshConversations();
    };

    const newChat = () => {
        setConversationId(null);
        setBubbles([]);
    };

    const send = async (text: string) => {
        const outgoing = text.trim();
        if (outgoing === '' || busy) {
            return;
        }
        setUserinput('');
        setBubbles(prev => [...prev, {role: 'user', content: outgoing}, {role: 'assistant', content: ''}]);
        setBusy(true);

        // Each event mutates the trailing assistant bubble in place, so text
        // appears as it streams rather than after the whole answer lands.
        const patch = (fn: (b: Bubble) => Bubble) =>
            setBubbles(prev => prev.map((b, i) => (i === prev.length - 1 ? fn(b) : b)));

        try {
            for await (const event of streamChat(outgoing, conversationId)) {
                switch (event.type) {
                    case 'meta':
                        if (event.conversation_id) setConversationId(event.conversation_id);
                        break;
                    case 'text':
                        patch(b => ({...b, content: b.content + (event.text ?? '')}));
                        break;
                    case 'tool':
                        patch(b => ({...b, tools: [...(b.tools ?? []), event.name ?? '']}));
                        break;
                    case 'chart':
                        patch(b => ({...b, charts: [...(b.charts ?? []), event.spec as ChartSpec]}));
                        break;
                    case 'snapshots':
                        patch(b => ({...b, snapshots: event.items ?? []}));
                        break;
                    case 'error':
                        patch(b => ({...b, content: event.message ?? 'Something went wrong.', error: true}));
                        break;
                    default:
                        break;
                }
            }
        } catch {
            patch(b => ({...b, content: 'Lost connection to the assistant.', error: true}));
        } finally {
            setBusy(false);
            refreshConversations();
        }
    };

    return (
        <div className="titanbot-page">
            <Header title="TitanBot AI" />

            <div className="chatbox">
                <aside className="chatbox-sidebar">
                    <button onClick={newChat} className="new-chat-button">
                        <IonIcon ios={addCircleOutline} md={addCircleSharp} />
                        New chat
                    </button>

                    <p className="chat-history-label">Conversations</p>
                    <div className="chat-history-list">
                        {conversations.map(conversation => (
                            <div
                                key={conversation.id}
                                className={`history-row${conversationId === conversation.id ? ' history-row--current' : ''}`}
                            >
                                <button
                                    type="button"
                                    className="history-row__open"
                                    onClick={() => openConversation(conversation.id)}
                                >
                                    {conversation.title || 'Untitled'}
                                </button>
                                <button
                                    type="button"
                                    className="history-row__delete"
                                    aria-label={`Delete ${conversation.title || 'conversation'}`}
                                    onClick={() => removeConversation(conversation.id)}
                                >
                                    <IonIcon ios={trashSharp} md={trashSharp} />
                                </button>
                            </div>
                        ))}
                    </div>
                </aside>

                <section className="chatbox-messages">
                    <div className="messages-list-scroll">
                        <div className="messages-list">
                            {bubbles.length === 0 && !busy && (
                                <div className="chat-empty">
                                    <span className="chat-empty__icon">
                                        <IonIcon ios={hardwareChipOutline} md={hardwareChipSharp} />
                                    </span>
                                    <h2 className="chat-empty__title">TitanBot</h2>
                                    <p className="chat-empty__text">
                                        Ask about live Missouri roadway conditions - incidents, queues,
                                        traffic, weather, flooding and crash-prone corridors.
                                    </p>
                                    <div className="chat-empty__suggestions">
                                        {SUGGESTIONS.map(suggestion => (
                                            <button
                                                type="button"
                                                key={suggestion}
                                                className="chat-suggestion"
                                                onClick={() => send(suggestion)}
                                            >
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {bubbles.map((bubble, index) => (
                                <div key={index} className={`chat-row chat-row--${bubble.role === 'user' ? 'user' : 'bot'}`}>
                                    <span className="chat-avatar" aria-hidden="true">
                                        {bubble.role === 'user'
                                            ? <IonIcon ios={personCircleOutline} md={personCircleSharp} />
                                            : <IonIcon ios={hardwareChipOutline} md={hardwareChipSharp} />}
                                    </span>
                                    <div className={`chat-bubble${bubble.error ? ' chat-bubble--error' : ''}`}>
                                        {bubble.tools && bubble.tools.length > 0 && (
                                            <div className="chat-tools">
                                                {bubble.tools.map((tool, i) => (
                                                    <span className="chat-tool" key={`${tool}-${i}`}>
                                                        checked {TOOL_LABELS[tool] ?? tool.replace(/_/g, ' ')}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {bubble.content
                                            ? <div className="message">{bubble.content}</div>
                                            : busy && index === bubbles.length - 1 && (
                                                <div className="chat-bubble--thinking">
                                                    <ThreeDot color="#898781" size="small" text="" textColor="" />
                                                    <span>Thinking</span>
                                                </div>
                                            )}

                                        {bubble.charts?.map((spec, i) => <ChatChart key={i} spec={spec} />)}

                                        {bubble.snapshots?.map(snap => (
                                            <figure className="chat-snapshot" key={snap.image_url}>
                                                <img src={snap.image_url} alt={snap.title} />
                                                <figcaption>{snap.title}</figcaption>
                                            </figure>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    <div className="user-input">
                        <form
                            className="input-container"
                            onSubmit={(e) => { e.preventDefault(); send(userinput); }}
                        >
                            <input
                                className="chat-input-textarea"
                                placeholder="Ask TitanBot about roadway conditions"
                                onChange={(e) => setUserinput(e.target.value)}
                                disabled={busy}
                                value={userinput}
                            />
                            <button
                                type="submit"
                                className="send-button"
                                aria-label="Send message"
                                disabled={busy || userinput.trim() === ''}
                            >
                                <IonIcon ios={sendOutline} md={sendSharp} />
                            </button>
                        </form>
                        <p className="input-hint">TitanBot can be wrong. Check anything you act on.</p>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default TitanBot;
